import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, Loader2, Minus, Plus } from 'lucide-react';

import { FALLBACK_PLANS, FALLBACK_ADDONS, type PublicPlan, type PublicAddon } from '@/data/plans';
import { getPublicPlans, getPublicAddons } from '@/lib/publicApi';
import { createBillingCheckout } from '@/lib/billingApi';
import { ApiError } from '@/lib/api';
import { useIsLoggedIn } from '@/lib/auth';
import { formatRub } from '@/utils/format';
import Reveal from '@/components/Reveal';

type Billing = 'month' | 'year';

export default function Pricing() {
  const [plans, setPlans] = useState<PublicPlan[]>(FALLBACK_PLANS);
  const [addons, setAddons] = useState<PublicAddon[]>(FALLBACK_ADDONS);
  const [billing, setBilling] = useState<Billing>('year');
  const [showCalc, setShowCalc] = useState(false);
  const authed = useIsLoggedIn();

  useEffect(() => {
    let cancelled = false;
    getPublicPlans()
      .then((res) => {
        if (cancelled) return;
        if (Array.isArray(res.plans) && res.plans.length > 0) setPlans(res.plans);
      })
      .catch(() => {
        /* тихий фолбек на FALLBACK_PLANS */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getPublicAddons()
      .then((res) => {
        if (cancelled) return;
        if (Array.isArray(res.addons) && res.addons.length > 0) setAddons(res.addons);
      })
      .catch(() => {
        /* тихий фолбек на FALLBACK_ADDONS */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const maxSavings = useMemo(
    () => Math.max(0, ...plans.map((p) => p.monthlySavingsPct)),
    [plans],
  );

  // Единый тариф сам мультизаведенческий: калькулятор строим на плане с доплатой
  // за доп. заведение (extraEstablishmentPriceMonth). Раньше это был только Network.
  const multiPlan = plans.find((p) => p.extraEstablishmentPriceMonth != null) ?? null;

  return (
    <section id="pricing" className="section section-divider scroll-mt-24">
      <div className="container-site">
        <div className="grid gap-10 md:grid-cols-12">
          <div className="md:col-span-6">
            <Reveal>
              <h2 className="h2">Платите за&nbsp;подписку, а&nbsp;не&nbsp;за&nbsp;оборот</h2>
            </Reveal>
          </div>
          <Reveal delay={80} className="md:col-span-5 md:col-start-8 md:pt-10">
            <p className="lead">
              Фиксированная плата в&nbsp;месяц и&nbsp;0&nbsp;% комиссии платформы, сколько&nbsp;бы
              вы ни&nbsp;заработали. Цена закреплена на&nbsp;год, НДС включён, чек по&nbsp;54-ФЗ.
            </p>
          </Reveal>
        </div>

        <Reveal delay={120}>
          <div className="mt-10 flex items-center justify-center">
            <BillingToggle value={billing} onChange={setBilling} savingsPct={maxSavings} />
          </div>
        </Reveal>

        <div
          className={
            plans.length === 1
              ? 'mt-10 w-full'
              : 'mt-10 grid grid-cols-1 gap-6 md:grid-cols-3'
          }
        >
          {plans.map((p, idx) => (
            <Reveal key={p.code} delay={idx * 80}>
              <PlanCard plan={p} billing={billing} authed={authed} wide={plans.length === 1} />
            </Reveal>
          ))}
        </div>

        {/* Калькулятор для сетей, под спойлером, чтобы не грузить одиночные кафе */}
        {multiPlan && (
          <Reveal delay={120}>
            <div className="mt-12 overflow-hidden rounded-3xl border border-line/70 bg-white shadow-soft">
              <button
                type="button"
                onClick={() => setShowCalc((v) => !v)}
                className="flex w-full items-center justify-between gap-4 px-7 py-6 text-left transition-colors hover:bg-paper sm:px-9"
                aria-expanded={showCalc}
              >
                <span>
                  <span className="block text-[11px] font-medium uppercase tracking-[0.14em] text-ink/45">
                    Сетям и франшизам
                  </span>
                  <span className="mt-1 block text-[16px] font-medium text-ink">
                    Рассчитать стоимость для нескольких заведений
                  </span>
                </span>
                <span
                  className={[
                    'grid h-9 w-9 shrink-0 place-items-center rounded-full bg-paper text-ink/60 transition-transform duration-300',
                    showCalc ? 'rotate-45' : '',
                  ].join(' ')}
                >
                  <Plus size={16} strokeWidth={1.75} />
                </span>
              </button>
              {showCalc && (
                <div className="border-t border-line/70">
                  <NetworkCalculator plan={multiPlan} billing={billing} />
                </div>
              )}
            </div>
          </Reveal>
        )}

        <p className="mt-8 text-center text-xs text-ink/50">
          Цена фиксируется на&nbsp;год · НДС включён, чек по&nbsp;54-ФЗ · Отмена в&nbsp;один клик
        </p>

        {/* Подробная сравнительная таблица */}
        <Reveal delay={80}>
          <ComparisonTable plans={plans} />
        </Reveal>

        {/* Модули (add-ons), подключаются к любому тарифу */}
        <Reveal delay={80}>
          <ModulesShowcase addons={addons} />
        </Reveal>
      </div>
    </section>
  );
}

// ─── Переключатель месяц / год ──────────────────────────────────────────
function BillingToggle({
  value,
  onChange,
  savingsPct,
}: {
  value: Billing;
  onChange: (v: Billing) => void;
  savingsPct: number;
}) {
  const base = 'rounded-full px-5 py-2.5 text-[13px] font-medium tracking-tight transition-colors duration-200';
  const active = 'bg-ink text-white shadow-soft';
  const idle = 'text-ink/55 hover:text-ink';
  return (
    <div className="inline-flex items-center rounded-full bg-paper p-1.5">
      <button
        type="button"
        onClick={() => onChange('month')}
        className={`${base} ${value === 'month' ? active : idle}`}
      >
        Ежемесячно
      </button>
      <button
        type="button"
        onClick={() => onChange('year')}
        className={`${base} ${value === 'year' ? active : idle} flex items-center gap-2`}
      >
        Годовая оплата
        {savingsPct > 0 && (
          <span
            className={[
              'inline-flex h-5 items-center rounded-full px-2 text-[11px] font-semibold uppercase tracking-[0.08em]',
              value === 'year' ? 'bg-orange text-white' : 'bg-orange/10 text-orange',
            ].join(' ')}
          >
            −{savingsPct} %
          </span>
        )}
      </button>
    </div>
  );
}

// ─── Карточка тарифа ────────────────────────────────────────────────────
function PlanCard({
  plan,
  billing,
  authed,
  wide = false,
}: {
  plan: PublicPlan;
  billing: Billing;
  authed: boolean;
  /** Единственный тариф, карточка растягивается в две колонки: цена слева, состав справа. */
  wide?: boolean;
}) {
  const popular = plan.isPopular;
  const monthly = billing === 'year' ? plan.priceYear / 12 : plan.priceMonth;
  const showStrike = billing === 'year' && plan.priceMonth > Math.round(monthly);

  // Для незалогиненных оставляем переход в /signup с предзаполненным планом -
  // после регистрации Signup автоматически запустит checkout (см. Signup.tsx).
  const params = new URLSearchParams({ plan: plan.code, billing });
  const signupHref = `/signup?${params.toString()}`;
  const ctaLabel = authed
    ? popular
      ? `Перейти на ${plan.name}`
      : `Выбрать ${plan.name}`
    : popular
      ? 'Начать 7 дней бесплатно'
      : `Выбрать ${plan.name}`;

  const [busy, setBusy] = useState(false);
  const handleCheckout = async () => {
    setBusy(true);
    try {
      const res = await createBillingCheckout({
        planCode: plan.code,
        billingPeriod: billing === 'year' ? 'YEARLY' : 'MONTHLY',
      });
      window.location.href = res.confirmationUrl;
    } catch (err) {
      setBusy(false);
      const msg =
        err instanceof ApiError ? err.message : 'Не удалось создать платёж. Попробуйте позже.';
      window.alert(msg);
    }
  };

  const buttonClass = popular
    ? 'btn-primary w-full px-6 py-3.5'
    : 'btn-outline w-full px-6 py-3.5';

  return (
    <article
      className={[
        'relative flex flex-col rounded-[2rem] border bg-white',
        wide ? 'md:grid md:grid-cols-12' : '',
        popular ? 'border-orange/40 shadow-orange-lift' : 'border-line shadow-soft',
      ].join(' ')}
    >
      {popular && (
        <div className="absolute -top-3.5 left-10">
          <span className="inline-flex h-7 items-center rounded-full bg-orange px-4 text-[11px] font-semibold uppercase tracking-[0.14em] text-white shadow-orange-lift">
            Рекомендуем
          </span>
        </div>
      )}

      <div
        className={[
          'flex flex-col gap-6 border-line p-9',
          wide ? 'border-b md:col-span-5 md:border-b-0 md:border-r md:p-11' : 'border-b',
        ].join(' ')}
      >
        <div>
          <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-ink/50">
            {plan.name}
          </div>
          {plan.tagline && (
            <div className="mt-2 text-[15px] text-ink/70">{plan.tagline}</div>
          )}
        </div>

        <div>
          <div className="flex items-baseline gap-2">
            <span className="text-[56px] font-medium leading-none tracking-tightest tabular-nums text-ink">
              {formatRub(Math.round(monthly)).replace('₽', '').trim()}
            </span>
            <span className="text-[15px] font-medium text-ink/55">₽ / мес</span>
            {showStrike && (
              <span className="text-[14px] font-medium tabular-nums text-ink/35 line-through">
                {formatRub(plan.priceMonth).replace('₽', '').trim()}
              </span>
            )}
          </div>
          <div className="mt-2 text-[12px] text-ink/55">
            {billing === 'year' ? (
              <>
                {formatRub(plan.priceYear)}{' '}
                <span className="text-ink/40">
                  / год · экономия {formatRub((plan.priceMonth * 12) - plan.priceYear)}
                </span>
              </>
            ) : (
              <>при ежемесячной оплате · НДС включён</>
            )}
          </div>

          {plan.extraEstablishmentPriceMonth !== null && (
            <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-paper px-3.5 py-1.5 text-[12px] text-ink/65">
              <Plus size={12} strokeWidth={1.75} className="text-orange" />
              {formatRub(plan.extraEstablishmentPriceMonth)} / мес за каждое следующее заведение
            </div>
          )}
        </div>

        {authed ? (
          <button
            type="button"
            onClick={handleCheckout}
            disabled={busy}
            className={`${buttonClass} disabled:opacity-60 disabled:cursor-not-allowed`}
          >
            {busy ? (
              <>
                <Loader2 size={16} strokeWidth={2} className="animate-spin" />
                Открываем оплату…
              </>
            ) : (
              <>
                {ctaLabel}
                <ArrowRight size={16} strokeWidth={2} />
              </>
            )}
          </button>
        ) : (
          <Link to={signupHref} className={buttonClass}>
            {ctaLabel}
            <ArrowRight size={16} strokeWidth={2} />
          </Link>
        )}
      </div>

      <div
        className={[
          'flex-1 p-9',
          wide
            ? 'rounded-b-[calc(2rem-1px)] bg-canvas/60 md:col-span-7 md:rounded-l-none md:rounded-r-[calc(2rem-1px)] md:p-11'
            : '',
        ].join(' ')}
      >
        <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-ink/50">
          Что входит
        </div>
        <ul className={['mt-6 gap-x-8 gap-y-3', wide ? 'grid sm:grid-cols-2' : 'space-y-3'].join(' ')}>
          {plan.highlights.map((f) => (
            <li
              key={f}
              className="flex items-start gap-3 text-[14px] leading-relaxed text-ink/85"
            >
              <Check size={16} strokeWidth={2} className="mt-0.5 shrink-0 text-orange" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      </div>
    </article>
  );
}

// ─── Калькулятор для сетевого тарифа ────────────────────────────────────
// Считает, во что обойдётся сеть целиком, но платят за каждое заведение
// отдельно: у каждой точки своя подписка. Кнопка оплачивает ПЕРВОЕ заведение,
// остальные владелец добавляет в админке — каждое получает свой пробный период
// и оплачивается из своей карточки.
function NetworkCalculator({ plan, billing }: { plan: PublicPlan; billing: Billing }) {
  const [count, setCount] = useState(3);
  const baseMonthly = billing === 'year' ? plan.priceYear / 12 : plan.priceMonth;
  const extra = plan.extraEstablishmentPriceMonth ?? 0;
  const totalMonthly = Math.round(baseMonthly + extra * Math.max(0, count - 1));
  const totalYear = totalMonthly * 12;
  const firstPrice = billing === 'year' ? plan.priceYear : plan.priceMonth;

  const authed = useIsLoggedIn();
  const [busy, setBusy] = useState(false);

  const startNetworkCheckout = async () => {
    setBusy(true);
    try {
      const res = await createBillingCheckout({
        planCode: plan.code,
        billingPeriod: billing === 'year' ? 'YEARLY' : 'MONTHLY',
      });
      window.location.href = res.confirmationUrl;
    } catch (err) {
      setBusy(false);
      const msg =
        err instanceof ApiError ? err.message : 'Не удалось создать платёж. Попробуйте позже.';
      window.alert(msg);
    }
  };

  return (
    <div className="grid gap-0 md:grid-cols-2">
      <div className="border-b border-line/70 p-9 md:border-b-0 md:border-r">
          <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-ink/45">
            Калькулятор сети
          </div>
          <h3 className="mt-3 text-[22px] font-medium tracking-tight text-ink">
            Сколько у&nbsp;вас заведений?
          </h3>
          <p className="mt-2 text-[13px] leading-relaxed text-ink/60">
            Первое заведение стоит {formatRub(plan.priceMonth)}/мес, каждое следующее
            {' '}{formatRub(extra)}/мес. Все функции включены. У каждого заведения своя
            подписка — оплачиваются они по отдельности.
          </p>

          <div className="mt-6 flex items-center gap-4">
            <button
              type="button"
              onClick={() => setCount((c) => Math.max(1, c - 1))}
              className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-line/70 text-ink transition-colors hover:bg-paper"
              aria-label="Меньше"
            >
              <Minus size={16} strokeWidth={1.75} />
            </button>
            <div className="flex flex-1 items-center justify-center rounded-full bg-paper px-6 py-3">
              <span className="text-[28px] font-medium tabular-nums leading-none text-ink">
                {count}
              </span>
              <span className="ml-2 text-[13px] text-ink/55">{declOf(count)}</span>
            </div>
            <button
              type="button"
              onClick={() => setCount((c) => Math.min(50, c + 1))}
              className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-line/70 text-ink transition-colors hover:bg-paper"
              aria-label="Больше"
            >
              <Plus size={16} strokeWidth={1.75} />
            </button>
          </div>

          <input
            type="range"
            min={1}
            max={20}
            step={1}
            value={Math.min(count, 20)}
            onChange={(e) => setCount(Number(e.target.value))}
            className="mt-5 h-1 w-full cursor-pointer appearance-none bg-line accent-orange"
            aria-label="Количество заведений"
          />
          <div className="mt-1 flex justify-between text-[11px] text-ink/40 tabular-nums">
            <span>1</span>
            <span>5</span>
            <span>10</span>
            <span>15</span>
            <span>20+</span>
          </div>
        </div>

        <div className="p-9">
          <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-ink/45">
            Итого за {count} {declOf(count)}
          </div>
          <div className="mt-3 flex items-baseline gap-3">
            <span className="text-[48px] font-medium leading-none tracking-tightest tabular-nums text-ink">
              {formatRub(totalMonthly).replace('₽', '').trim()}
            </span>
            <span className="text-[14px] font-medium text-ink/55">₽ / мес</span>
          </div>
          <div className="mt-2 text-[12px] text-ink/55">
            {billing === 'year' ? (
              <>{formatRub(totalYear)} в год · экономия {plan.monthlySavingsPct}%</>
            ) : (
              <>при ежемесячной оплате · НДС включён</>
            )}
          </div>

          <ul className="mt-6 space-y-2 border-t border-line/70 pt-5 text-[13px] text-ink/70">
            <li className="flex items-start gap-3">
              <Check size={14} strokeWidth={2} className="mt-1 shrink-0 text-orange" />
              Все функции Resstro на каждом заведении
            </li>
            <li className="flex items-start gap-3">
              <Check size={14} strokeWidth={2} className="mt-1 shrink-0 text-orange" />
              Все точки в одном кабинете, переключение в один клик
            </li>
            <li className="flex items-start gap-3">
              <Check size={14} strokeWidth={2} className="mt-1 shrink-0 text-orange" />
              Одна касса на заведение · оплата картой гостем (ЮKassa)
            </li>
          </ul>

          {authed ? (
            <button
              type="button"
              onClick={startNetworkCheckout}
              disabled={busy}
              className="btn-dark mt-7 w-full px-6 py-3.5 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {busy ? (
                <>
                  <Loader2 size={16} strokeWidth={2} className="animate-spin" />
                  Открываем оплату…
                </>
              ) : (
                <>
                  Начать с первого заведения · {formatRub(firstPrice)}
                  <ArrowRight size={16} strokeWidth={2} />
                </>
              )}
            </button>
          ) : (
            <Link
              to={`/signup?plan=${plan.code}&billing=${billing}`}
              className="btn-dark mt-7 w-full px-6 py-3.5"
            >
              Начать с первого заведения
              <ArrowRight size={16} strokeWidth={2} />
            </Link>
          )}

          {count > 1 && (
            <p className="mt-3 text-[12px] text-ink/55">
              Сейчас оплачивается одно заведение. Остальные {count - 1}{' '}
              {declOf(count - 1)} добавите в админке — у каждого своя подписка и оплата.
            </p>
          )}

          {count > 10 && (
            <p className="mt-3 text-[12px] text-ink/55">
              Для сетей от 10 заведений индивидуальные условия. Напишем сами после регистрации.
            </p>
          )}
        </div>
      </div>
  );
}

function declOf(n: number) {
  const m = Math.abs(n) % 100;
  const m1 = m % 10;
  if (m > 10 && m < 20) return 'заведений';
  if (m1 > 1 && m1 < 5) return 'заведения';
  if (m1 === 1) return 'заведение';
  return 'заведений';
}

// ─── Витрина модулей (add-ons) ─────────────────────────────────────────
function ModulesShowcase({ addons }: { addons: PublicAddon[] }) {
  const [open, setOpen] = useState(false);
  if (addons.length === 0) return null;
  return (
    <div className="mt-5 overflow-hidden rounded-3xl border border-line/70 bg-white shadow-soft">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-4 px-7 py-6 text-left transition-colors hover:bg-paper sm:px-9"
        aria-expanded={open}
      >
        <span>
          <span className="block text-[11px] font-medium uppercase tracking-[0.14em] text-ink/45">
            Модули
          </span>
          <span className="mt-1 block text-[16px] font-medium text-ink">
            Дополнительные модули подключаются к&nbsp;любому тарифу
          </span>
        </span>
        <span
          className={[
            'grid h-9 w-9 shrink-0 place-items-center rounded-full bg-paper text-ink/60 transition-transform duration-300',
            open ? 'rotate-45' : '',
          ].join(' ')}
        >
          <Plus size={16} strokeWidth={1.75} />
        </span>
      </button>

      {open && (
        <div className="border-t border-line/70">
          <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-3">
            {addons.map((a) => (
              <div key={a.code} className="flex flex-col rounded-2xl bg-paper p-6">
                <h4 className="text-[15px] font-medium tracking-tight text-ink">{a.name}</h4>
                <p className="mt-2 flex-1 text-[13px] leading-relaxed text-ink/60">{a.description}</p>
                <div className="mt-5 text-[14px] font-medium text-ink">
                  {formatRub(a.priceMonth)}
                  <span className="text-ink/45">/мес</span>
                </div>
              </div>
            ))}
          </div>
          <p className="px-7 pb-6 text-center text-xs text-ink/50 sm:px-9">
            Модули подключает менеджер по&nbsp;запросу. Стоимость добавляется к&nbsp;абонплате тарифа.
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Сравнительная таблица ─────────────────────────────────────────────
const COMPARE_ROWS: { group: string; rows: { label: string; values: (string | boolean)[] }[] }[] = [
  {
    group: 'Лимиты',
    rows: [
      { label: 'Заведений', values: ['1', '1', 'без лимита'] },
      { label: 'Касс на iPad / iPhone', values: ['до 2', 'до 5', 'без лимита'] },
      { label: 'Сотрудников', values: ['до 3', 'до 10', 'без лимита'] },
      { label: 'История заказов', values: ['вся', 'вся', 'вся'] },
    ],
  },
  {
    group: 'Зал и касса',
    rows: [
      { label: 'QR-меню', values: [true, true, true] },
      { label: 'Печать чеков по локальной сети', values: [true, true, true] },
      { label: 'Работа в офлайне', values: [true, true, true] },
      { label: 'Перетаскивание столиков и переносы', values: [true, true, true] },
    ],
  },
  {
    group: 'Деньги и право',
    rows: [
      { label: 'Онлайн-оплата гостем (ЮKassa, СБП)', values: [false, true, true] },
      { label: 'Кассовые чеки по закону (54-ФЗ)', values: [true, true, true] },
      { label: 'Экспорт смен в Excel / PDF', values: [true, true, true] },
      { label: 'Прибыльность блюд и продажи по официантам', values: [false, true, true] },
    ],
  },
  {
    group: 'Управление',
    rows: [
      { label: 'Журнал действий сотрудников', values: [false, true, true] },
      { label: 'Возвращаемость гостей и аналитика', values: [false, true, true] },
      { label: 'Сводный отчёт по всей сети', values: [false, false, true] },
      { label: 'QR-меню под своим брендом и доменом', values: [false, false, true] },
      { label: 'Подключение к 1С, сайту и аналитике', values: [false, false, true] },
    ],
  },
  {
    group: 'Поддержка',
    rows: [
      { label: 'Поддержка по email', values: [true, true, true] },
      { label: 'Приоритетная поддержка в WhatsApp', values: [false, true, true] },
      { label: 'Персональный менеджер', values: [false, false, true] },
      { label: 'Гарантия работы и ответ за час', values: [false, false, true] },
    ],
  },
];

function ComparisonTable({ plans }: { plans: PublicPlan[] }) {
  const [open, setOpen] = useState(false);
  const order: string[] = ['start', 'business', 'network'];
  const sorted = order.map((c) => plans.find((p) => p.code === c)).filter(Boolean) as PublicPlan[];
  // Единый тариф: сравнивать не с чем, таблицу сравнения не показываем.
  if (sorted.length < 2) return null;
  return (
    <div className="mt-5 overflow-hidden rounded-3xl border border-line/70 bg-white shadow-soft">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-4 px-7 py-6 text-left transition-colors hover:bg-paper sm:px-9"
        aria-expanded={open}
      >
        <span>
          <span className="block text-[11px] font-medium uppercase tracking-[0.14em] text-ink/45">
            Подробное сравнение
          </span>
          <span className="mt-1 block text-[16px] font-medium text-ink">
            Что именно входит в каждый тариф
          </span>
        </span>
        <span
          className={[
            'grid h-9 w-9 shrink-0 place-items-center rounded-full bg-paper text-ink/60 transition-transform duration-300',
            open ? 'rotate-45' : '',
          ].join(' ')}
        >
          <Plus size={16} strokeWidth={1.75} />
        </span>
      </button>

      {open && (
        <div className="overflow-x-auto border-t border-line/70">
          <table className="w-full min-w-[760px] border-collapse text-left text-[14px]">
            <thead>
              <tr className="bg-paper">
                <th className="border-b border-line/70 px-7 py-4 text-[11px] font-medium uppercase tracking-[0.14em] text-ink/45 sm:px-9">
                  Возможность
                </th>
                {sorted.map((p) => (
                  <th
                    key={p.code}
                    className="border-b border-l border-line/70 px-6 py-4 text-[13px] font-medium text-ink"
                  >
                    {p.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARE_ROWS.map((g) => (
                <>
                  <tr key={g.group} className="bg-paper/60">
                    <td
                      colSpan={1 + sorted.length}
                      className="border-b border-line/70 px-7 py-3 text-[11px] font-medium uppercase tracking-[0.14em] text-ink/55 sm:px-9"
                    >
                      {g.group}
                    </td>
                  </tr>
                  {g.rows.map((r) => (
                    <tr key={r.label} className="hover:bg-paper/50">
                      <td className="border-b border-line/70 px-7 py-3 text-ink/85 sm:px-9">
                        {r.label}
                      </td>
                      {r.values.map((v, i) => (
                        <td
                          key={i}
                          className="border-b border-l border-line/70 px-6 py-3 align-middle text-[14px] text-ink/80"
                        >
                          {typeof v === 'boolean' ? (
                            v ? (
                              <Check size={16} strokeWidth={2} className="text-orange" />
                            ) : (
                              <Minus size={16} strokeWidth={1.75} className="text-ink/25" />
                            )
                          ) : (
                            <span>{v}</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
